import styled, { css } from 'styled-components';
import { Link, Text } from '@bufferapp/ui';
import { borderRadius } from '@bufferapp/ui/style/borders';
import {
  grayLight,
  grayShadow,
  white,
  gray,
  grayLighter,
  redDark,
  redDarker,
  red,
  grayDark,
  blueLight,
  orangeLight,
  greenLight,
} from '@bufferapp/ui/style/colors';
import {
  fontSize,
  fontWeightMedium,
  fontSizeSmall,
} from '@bufferapp/ui/style/fonts';

const size = {
  medium: '1555px',
};

const PreviewPositionLeft = css`
  left: -17px;
  transform: translateX(-100%);
`;

const LeftArrow = css`
  content: '';
  position: absolute;
  border-bottom: 1px solid ${gray};
  border-left: 1px solid ${gray};
  background: linear-gradient(46deg, ${grayLighter} 50%, ${white} 50%);
  transform: rotate(45deg);
  bottom: 0px;
  left: -11px;
  top: 55px;
  width: 20px;
  height: 20px;
  z-index: -1;
`;

const BottomArrow = css`
  top: auto;
  bottom: 10px;
  background: ${white};
`;

const RightArrow = css`
  left: auto;
  right: -11px;
  border-left: none;
  border-bottom: none;
  border-right: 1px solid ${gray};
  border-top: 1px solid ${gray};
  background: linear-gradient(46deg, ${white} 50%, ${grayLighter} 50%);
`;

const RightGap = css`
  right: -18px;
  left: auto;
`;
const LeftGap = css`
  left: -18px;
  right: auto;
`;

export const PostPreviewWrapper = styled.div`
  border-radius: ${borderRadius};
  visibility: hidden;
  opacity: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid ${gray};
  box-shadow: ${grayShadow};
  background: ${white};
  box-sizing: border-box;
  position: absolute;
  z-index: 3;
  width: 364px;
  left: calc(100% + 17px);
  transform: translateX(0);
  top: -50px;
  transition: all 0.3s ease-in-out;

  ${(props) =>
    props.inLastTwoRows &&
    css`
      top: auto;
      bottom: -5px;
    `}

  ${(props) => props.isEndOfWeek && PreviewPositionLeft}

  @media (max-width: ${size.medium}) {
    ${(props) => props.isLastThirdDayOfWeek && PreviewPositionLeft}
  }

  &::before {
    ${LeftArrow}
    ${(props) => props.isEndOfWeek && RightArrow}
    ${(props) => props.inLastTwoRows && BottomArrow}

    @media (max-width: ${size.medium}) {
      ${(props) => props.isLastThirdDayOfWeek && RightArrow}
    }
  }

  // This is used to prevent the preview from disappearing when moving in the gap between the post and the popup
  &::after {
    content: '';
    width: 18px;
    height: 100%;
    position: absolute;
    bottom: 0;
    ${(props) => (props.isEndOfWeek ? RightGap : LeftGap)}

    @media (max-width: ${size.medium}) {
      ${(props) =>
        props.isEndOfWeek || props.isLastThirdDayOfWeek ? RightGap : LeftGap}
    }
  }
`;

export const DraftWrapper = styled.div`
  display: flex;
  align-items: center;
  background-color: ${(props) =>
    props.needsApproval ? orangeLight : blueLight};
  padding: 8px;
  color: ${white};
`;

export const BlueprintWrapper = styled.div`
  display: flex;
  align-items: center;
  background-color: ${greenLight};
  padding: 8px;
  color: ${white};
`;

export const DraftWrapperText = styled.span`
  margin-left: 8px;
  font-weight: ${fontWeightMedium};
  font-size: ${fontSize};
`;

export const ErrorWrapper = styled.div`
  display: flex;
  background-color: ${redDark};
  padding: 8px;
`;

export const ParagraphText = styled(Text)`
  margin: 0 0 0 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: ${fontWeightMedium};
`;

export const ParagraphErrorText = styled(ParagraphText)`
  white-space: normal;
  width: 100%;
`;

export const ChannelDescriptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

export const ChannelName = styled(ParagraphText)`
  max-width: 132px;
`;

export const ChannelSubtitle = styled(ParagraphText)`
  max-width: 132px;
  font-size: ${fontSizeSmall};
`;

export const PostPreviewHeader = styled.div`
  padding: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const FlexWrapper = styled.div`
  display: flex;
  align-items: center;
  > :not(:first-child) {
    margin-left: 8px;
  }
`;

export const PostTitle = styled(Text)`
  margin: 0 0 8px;
  font-weight: ${fontWeightMedium};
`;

export const PostLink = styled(Text)`
  margin: 0 0 8px;
`;

export const PostText = styled(Text)`
  margin: 0;
`;

export const Content = styled.div`
  border-top: 1px solid ${grayLight};
  padding: 16px;
  flex-direction: column;
  display: flex;
  background-color: ${grayLighter};
  max-height: 280px;
  overflow: auto;
`;

export const MediaWrapper = styled.div`
  display: flex;
  align-items: flex-start;

  > :not(:first-child) {
    margin-left: 8px;
  }
`;

export const TextWrapper = styled.div`
  text-align: left;
  flex: 1;

  p {
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const AttachmentCard = styled.div`
  background-color: ${white};
  border-radius: ${borderRadius};
  border: 1px solid ${grayLight};
  padding: 16px;
  margin-top: 8px;
`;

export const PostPreviewFooter = styled.div`
  border-top: 1px solid ${grayLight};
  padding: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: ${(props) =>
    props.alignContentToRight ? 'flex-end' : 'flex-start'};
  min-height: 32px;
`;

export const BellIconWrapper = styled.div`
  margin-top: ${(props) => (props.small ? '0' : '6px')};
  margin-left: ${(props) => (props.small ? '8px' : '0')};
  background-color: ${red};
  border-radius: 100%;
  height: 16px;
  width: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  svg {
    height: 12px;
    width: 12px;
  }
`;

export const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  display: flex;
  svg {
    fill: ${grayDark};
  }
`;

export const ThreadIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  svg {
    height: 14px;
    width: 14px;
    margin-right: 4px;
    margin-top: 2px;
  }
`;

export const ThreadCounter = styled.span`
  color: ${grayDark};
  font-weight: 500;
  font-family: Roboto, sans-serif;
  font-size: 14px;
`;

export const PhoneIconWrapper = styled.div`
  margin-top: ${(props) => (props.small ? '0' : '6px')};
  margin-left: ${(props) => (props.small ? '8px' : '0')};
  background-color: ${grayDark};
  border-radius: 100%;
  height: 16px;
  width: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  svg {
    height: 10px;
    width: 10px;
  }
`;

export const StyledLink = styled(Link)`
  color: ${white};
  padding: 8px 10px;
  margin-left: 2px;
  height: 16px;
  background-color: ${redDarker};
  border-radius: 5px;

  :hover {
    color: ${white} !important;
  }

  :first-letter {
    text-transform: capitalize;
  }
`;
